import React from 'react'
// @src imports
import { Confirm, Icon } from 'src/chrome'
import { useState } from 'src/hooks'
import { defaultFragment } from 'src/editor/text/ElementText'
// relative imports
import styles from './editorToolBar.module.scss'
import { Api } from '../../api'
import { Steps } from '../../types'
import { isCustomCard as getIsCustomCard } from '../../../redux/selectors/editor'

interface Props {
  api: Api
}

const EditorToolBar: React.FC<Props> = props => {
  const { api } = props
  const {
    step,
    selectedLayoutId,
    updatePanel,
    handleSave,
    card,
    fullBleeds,
    onClose,
    updateLayout,
    selectedFullBleed,
    isCreatingBackPanel,
    setIsCreatingBackPanel,
  } = api
  const { fullBleedIndex, panelIndex } = step

  const [showConfirmVisible, setShowConfirmVisible] = useState(false)
  const isCustomCard = getIsCustomCard({
    ...card,
    sendableCard: card.detailedSendableCard,
  })

  const isBackPanel =
    fullBleeds.length > 2 && fullBleedIndex === fullBleeds.length - 1

  const isPanelLocked =
    fullBleedIndex === 0 && panelIndex === 0 && !isCustomCard

  const isSaveLayoutVisible =
    fullBleedIndex === fullBleeds.length - 1 &&
    card.type !== 'POSTCARD' &&
    card.type !== 'FLATCARD'

  const handleAddTextElement =
    fullBleeds[0].panels[0].elements.length < 2
      ? () =>
          updatePanel(0, 0, panel => ({
            ...panel,
            elements: panel.elements.concat([
              {
                x: 0.1,
                y: 0.1,
                z: panel.elements.length + 1,
                rotation: 0,
                width: 0.5,
                height: 0.2,
                locked: false,
                text: [defaultFragment],
                image: null,
              },
            ]),
          }))
      : () => {}

  const editLayout = () => {
    api.editLayout(fullBleedIndex, panelIndex ?? 0)
    if (isBackPanel) setIsCreatingBackPanel(!isCreatingBackPanel)
  }

  return (
    <div className={styles.editorPanel}>
      <nav>
        <div
          className={styles.editorPanelLogo}
          onClick={() => setShowConfirmVisible(true)}
        >
          <div className={styles.logo}>
            <Icon icon={'ARROWLEFT'} size={17} color={'#6f8394'} />
            <p>Catalog</p>
          </div>
        </div>
        {!isPanelLocked && (
          <div className={styles.itemContainer}>
            {step.fullBleedIndex === 0 && isCustomCard && (
              <div
                className={styles.menu}
                onClick={() => handleAddTextElement()}
                id={'editor_textbox_btn'}
              >
                <Icon
                  icon={'TEXT'}
                  stroke={'#333'}
                  color={'none'}
                  strokeWidth={'46.5455'}
                  viewBox={'0 0 838 1024'}
                />
                <p>Textbox</p>
              </div>
            )}

            <div
              id={'editor_templates_btn'}
              className={`${styles.menu} ${
                step.type === Steps.EditLayout ? styles.selected : ''
              }`}
              onClick={() => editLayout()}
            >
              <Icon
                icon={'TEMPLATES'}
                stroke={'#333'}
                color={'none'}
                strokeWidth={'46.5455'}
                viewBox={'0 0 838 1024'}
              />
              <p>Layouts</p>
            </div>

            <div
              id={'editor_panel_background_btn'}
              className={`${styles.menu} ${
                step.type === Steps.EditBackgroundColor ? styles.selected : ''
              }`}
              onClick={() =>
                api.editBackgroundColor(fullBleedIndex, panelIndex ?? 0)
              }
            >
              <Icon
                icon={'PANELBACKGROUND'}
                stroke={'#333'}
                color={'none'}
                strokeWidth={'46.5455'}
                viewBox={'0 0 838 1024'}
              />
              <p>Background</p>
            </div>

            {(step.type === Steps.EditPicture ||
              step.type === Steps.EditBackgroundPicture) && (
              <>
                <div className={`${styles.menu} ${styles.selected}`}>
                  <Icon icon={'PHOTOS'} size={20} />
                  <p>Images</p>
                </div>

                {(() => {
                  const elementHasImage =
                    step.type === Steps.EditPicture &&
                    api.getSelectedElement(step)
                  const backgroundHasImage =
                    step.type === Steps.EditBackgroundPicture &&
                    selectedFullBleed.backgroundElement?.image?.image

                  if (elementHasImage || backgroundHasImage) {
                    return (
                      <div
                        className={`${styles.menu}`}
                        onClick={() =>
                          api.removePicture(
                            step.fullBleedIndex,
                            step.panelIndex,
                            step.elementIndex ?? 0,
                          )
                        }
                      >
                        <Icon icon={'REMOVE'} />
                        <p>Remove</p>
                      </div>
                    )
                  }

                  return null
                })()}
              </>
            )}

            <div className={styles.itemBottom}>
              {isSaveLayoutVisible && selectedLayoutId && (
                <div
                  id="save_layout_btn"
                  className={`${styles.menu}`}
                  onClick={() => updateLayout(selectedLayoutId)}
                >
                  <Icon icon={'SAVE'} />
                  <p>Save Layout</p>
                </div>
              )}
              <div className={`${styles.menu}`} onClick={() => handleSave()}>
                <Icon icon={'SAVE'} />
                <p>Save</p>
              </div>
            </div>
          </div>
        )}
      </nav>
      <div />
      {showConfirmVisible && (
        <Confirm
          title={'You have made unsaved changes to this card'}
          message={
            "This card will be available in your Account under 'Drafted Cards'. Any unsaved changes will be lost."
          }
          confirmTitle="Continue without saving"
          declineTitle="Return to card editor"
          onConfirm={onClose}
          wrapperClassName={styles.confirmWrapper}
          onDecline={() => setShowConfirmVisible(false)}
        />
      )}
    </div>
  )
}

export default EditorToolBar
